/**
 * Smer prema istom periodu prošle godine — pravilo, bez teksta i bez boje.
 *
 * Procenat nije uvek odgovor. Artikal 350 je u septembru 2026 prodao 99.160 kg prema 460 kg lane,
 * pa je tabela pisala „▲ 21.456,5 %": tačno, i beskorisno. Isto tako, odnos dva negativna RUC-a
 * ispada pozitivan broj iako je gubitak porastao, a odnos prema nuli ne postoji.
 *
 * Zato ovo vraća VRSTU promene, a tekst i boju bira onaj ko crta — ekran na svom jeziku, dokument
 * na jeziku čitaoca.
 */

/** Preko ovog odnosa procenat prestaje da se čita i prelazi se na „×N". */
const PRAG_PUTA = 10;

export type Promena =
  /** Nema se šta uporediti: obe strane nula, ili nedostaje podatak. */
  | { vrsta: "nema" }
  /** Prošle godine nije bilo prodaje, ove ima. */
  | { vrsta: "novo" }
  /** Ove godine nema prodaje, prošle je bilo. */
  | { vrsta: "nestalo" }
  /** Iz dobitka u gubitak, ili iz nule u minus. */
  | { vrsta: "uGubitak" }
  /** Iz gubitka u dobitak. */
  | { vrsta: "izGubitka" }
  /** Oba perioda u minusu, ovaj dublje. */
  | { vrsta: "veciGubitak" }
  /** Oba perioda u minusu, ovaj pliće. */
  | { vrsta: "manjiGubitak" }
  /** Odnos je toliki da procenat gubi smisao. */
  | { vrsta: "puta"; faktor: number }
  | { vrsta: "procenat"; procenat: number };

/** Kako red treba da se oboji, kad se zna da li je rast dobar. */
export type Ton = "dobro" | "lose" | "mirno" | "novo";

export function promena(sada: number | null | undefined, pre: number | null | undefined): Promena {
  const s = sada == null || !Number.isFinite(sada) ? null : sada;
  const p = pre == null || !Number.isFinite(pre) ? null : pre;
  if (s === null || p === null) return { vrsta: "nema" };
  if (p === 0 && s === 0) return { vrsta: "nema" };
  if (p === 0) return s > 0 ? { vrsta: "novo" } : { vrsta: "uGubitak" };
  if (s === 0) return { vrsta: "nestalo" };
  // Promena znaka: odnos postoji kao broj, ali ne znači ništa. Sojina sačma je prešla sa
  // −702.786,03 na −11.133.474,28 — odnos je +15,8, a gubitak je petnaest puta veći.
  if (p < 0 && s < 0) {
    return Math.abs(s) > Math.abs(p) ? { vrsta: "veciGubitak" } : { vrsta: "manjiGubitak" };
  }
  if (p < 0) return { vrsta: "izGubitka" };
  if (s < 0) return { vrsta: "uGubitak" };

  const odnos = s / p;
  if (odnos >= PRAG_PUTA || odnos <= 1 / PRAG_PUTA) return { vrsta: "puta", faktor: odnos };
  return { vrsta: "procenat", procenat: (odnos - 1) * 100 };
}

/**
 * Boja za datu promenu.
 *
 * `viseJeBolje` je obavezno jer isti smer ne znači isto: rast prodaje je dobar, rast dospelog
 * potraživanja nije.
 */
export function ton(p: Promena, viseJeBolje: boolean): Ton {
  switch (p.vrsta) {
    case "nema":
      return "mirno";
    case "novo":
      return viseJeBolje ? "novo" : "lose";
    case "nestalo":
      return viseJeBolje ? "lose" : "dobro";
    case "uGubitak":
    case "veciGubitak":
      return "lose";
    case "izGubitka":
    case "manjiGubitak":
      return "dobro";
    case "puta":
      return p.faktor >= 1 === viseJeBolje ? "dobro" : "lose";
    case "procenat":
      if (p.procenat === 0) return "mirno";
      return p.procenat > 0 === viseJeBolje ? "dobro" : "lose";
  }
}
